import { EditRegular } from "@fluentui/react-icons";
import { adminGetDeliveryMethodConfigs } from "apps/project-iq/api";
import { IDeliveryMethodConfig } from "apps/project-iq/models/IDeliveryMethodConfig";
import {
  ALL_DIAGNOSTIC_METHODS,
  METHOD_LABELS,
} from "apps/project-iq/utils/diagnosticUtils";
import { AdminListPage } from "components/adminList/AdminListPage";
import { IAdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { ITableColumn } from "models/ITableColumn";
import { useEffect, useMemo, useState } from "react";
import { EditDeliveryMethodDialog } from "./EditDeliveryMethodDialog";
import { useManageDeliveryMethodsStyles } from "./ManageDeliveryMethods.styles";
import {
  IDialogState,
  IDeliveryMethodRow,
} from "./ManageDeliveryMethods.types";

const CONFIGS_PER_PAGE = 15;
const CONFIGS_ACTIONS_COLUMN_WIDTH = "95px";

export const ManageDeliveryMethods = (): JSX.Element => {
  const colStyles = useAdminColumnStyles();
  const methodStyles = useManageDeliveryMethodsStyles();
  const [configs, setConfigs] = useState<IDeliveryMethodConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<IDialogState>({
    open: false,
    method: null,
    config: null,
  });

  const loadConfigs = async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await adminGetDeliveryMethodConfigs();
      setConfigs(data as IDeliveryMethodConfig[]);
    } catch {
      setError("Failed to load delivery method configs. Please try again.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void loadConfigs();
  }, []);

  const handleSave = (): void => {
    setDialogState({ open: false, method: null, config: null });
    void loadConfigs();
  };

  const handleDismiss = (): void => {
    setDialogState({ open: false, method: null, config: null });
  };

  // Build rows by merging all 4 methods with existing configs
  const rows: IDeliveryMethodRow[] = useMemo(
    () =>
      ALL_DIAGNOSTIC_METHODS.map((method) => {
        const existingConfig = configs.find((cfg) => cfg.method === method);
        return {
          method,
          config: existingConfig || null,
        };
      }),
    [configs],
  );

  const columns: ITableColumn<IDeliveryMethodRow>[] = useMemo(
    () => [
      {
        columnId: "method",
        displayValue: "Delivery Method",
        className: colStyles.normal,
        renderHeaderCell: () => "Delivery Method",
        compare: (a: IDeliveryMethodRow, b: IDeliveryMethodRow) =>
          METHOD_LABELS[a.method].localeCompare(METHOD_LABELS[b.method]),
        renderCell: (item: IDeliveryMethodRow) => (
          <span>{METHOD_LABELS[item.method]}</span>
        ),
      },
      {
        columnId: "prompt",
        displayValue: "Prompt",
        className: colStyles.wide,
        renderHeaderCell: () => "Prompt",
        compare: (a: IDeliveryMethodRow, b: IDeliveryMethodRow) => {
          const promptA = a.config?.prompt || "";
          const promptB = b.config?.prompt || "";
          return promptA.localeCompare(promptB);
        },
        renderCell: (item: IDeliveryMethodRow) => {
          if (!item.config?.prompt) {
            return (
              <span className={methodStyles.noPromptText}>
                No prompt configured
              </span>
            );
          }
          const truncatedPrompt =
            item.config.prompt.length > 80
              ? `${item.config.prompt.substring(0, 80)}...`
              : item.config.prompt;
          return (
            <span
              className={methodStyles.promptCell}
              title={item.config.prompt}
            >
              {truncatedPrompt}
            </span>
          );
        },
      },
    ],
    [colStyles, methodStyles],
  );

  const rowActions: IAdminRowAction<IDeliveryMethodRow>[] = useMemo(
    () => [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item: IDeliveryMethodRow) =>
          setDialogState({
            open: true,
            method: item.method,
            config: item.config,
          }),
      },
    ],
    [],
  );

  return (
    <>
      {error && (
        <div role="alert" className={methodStyles.errorAlert}>
          {error}
        </div>
      )}
      <AdminListPage<IDeliveryMethodRow>
        title="Delivery Methods"
        items={rows}
        columns={columns}
        isLoading={loading}
        searchPlaceholder="Search delivery methods..."
        searchQuery=""
        onSearchChange={() => {}}
        rowActions={rowActions}
        getRowId={(item) => item.method}
        paginatedItems={rows}
        selectedPageIndex={0}
        pageCount={1}
        totalItemsCount={rows.length}
        itemsPerPage={CONFIGS_PER_PAGE}
        onPageChange={() => {}}
        emptyStateMessage="No delivery methods found."
        actionsWidth={CONFIGS_ACTIONS_COLUMN_WIDTH}
      />
      {dialogState.method !== null && (
        <EditDeliveryMethodDialog
          open={dialogState.open}
          method={dialogState.method}
          config={dialogState.config}
          onSave={handleSave}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
};
